import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Card, Tabs, Table, Tag, Button, Space, Modal, Alert, Input, InputNumber, Select, message, Empty, Badge, Tooltip, Statistic } from 'antd';
import {
  CheckCircleOutlined, CloseCircleOutlined, PauseCircleOutlined, ArrowUpOutlined, ReloadOutlined, PlusOutlined,
  AuditOutlined, SafetyCertificateOutlined, SearchOutlined
} from '@ant-design/icons';
import useAuth from '../../../../hooks/useAuth';
import './proformaApprovals.css';
import { proformaApprovalApi } from './proformaApprovalApi';
import {
  APPROVAL_TABS, inboxFilterForTab, approverActionsFor,
  approvalLevelColor, approvalLevelLabel, requestedActionLabel
} from './proformaApprovalHelpers';
// Stage SALES-RECEIPTS-1 — the Accounts (L2) review reads the live customer
// position (operational exposure) instead of typed figures, and can record a
// receipt inline before approving.
import { customerReceiptsApi } from '../../accounts/customerReceipts/customerReceiptsApi';
import {
  overLimitMessage, canRecordReceipt, canPostReceipt, RECEIPT_MODES, modeLabel
} from '../../accounts/customerReceipts/customerReceiptsHelpers';

// ════════════════════════════════════════════════════════════════════
// Stage SALES-PROFORMA-APPROVAL-1 — Proforma Approval Inbox (Option B).
//
// A dense control grid: a KPI strip of what's waiting by level, tabs with live
// counts, and one row per request carrying the live Outstanding exposure and
// level-appropriate quick-actions. Selecting a row (or a quick-action) opens a
// compact decision popup that puts the money/credit facts in front of the
// approver — the live position, the over-limit steer, an inline receipt, and
// the decision buttons. No Work Order is created here.
// ════════════════════════════════════════════════════════════════════

function backendError(err, fallback) {
  return (err && err.response && err.response.data && err.response.data.error && err.response.data.error.message)
    || (err && err.message) || fallback || 'Something went wrong.';
}
function money(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  const sign = n < 0 ? '-' : '';
  return sign + '₹' + Math.abs(n).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
function dateTime(v) {
  if (!v) return '—';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' });
}
// Compact date for the grid — drops the year so the time never truncates.
function dateShort(v) {
  if (!v) return '—';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', hour12: true });
}
const REQ_STATUS_COLOR = { PENDING: 'gold', APPROVED: 'green', REJECTED: 'red', HELD: 'orange', ESCALATED: 'blue', CANCELLED: 'default' };
// Shared section-title class for the popup's section headings.
const SEC = 'text-[10.5px] font-bold uppercase tracking-wide text-slate-400';

// One decision spec drives every action; `kind` selects the fields + api call.
// L1 (status change) is decided on the list drawer, so the inbox only carries
// Accounts (L2) and credit-override (L3) decisions.
const DECISION = {
  accountsApprove: { ok: 'Approve & mark WO Ready', danger: false, needsReason: false, payment: true },
  accountsReject:  { ok: 'Reject', danger: true, needsReason: true, payment: false },
  accountsHold:    { ok: 'Hold', danger: false, needsReason: true, payment: false },
  accountsEscalate:{ ok: 'Escalate', danger: false, needsReason: true, payment: true },
  l3Approve:       { ok: 'Approve', danger: false, needsReason: true, payment: false },
  l3Reject:        { ok: 'Reject', danger: true, needsReason: true, payment: false }
};

const KPI = [
  { key: 'ACCOUNTS', label: 'Accounts waiting', level: 'L2', tab: 'ACCOUNTS' },
  { key: 'L3', label: 'Management approvals', level: 'L3', tab: 'L3' }
];
const KPI_ACCENT = { L2: '#b45309', L3: '#be123c', null: '#059669' };
const KPI_ICON = {
  ACCOUNTS: <AuditOutlined />,
  L3: <SafetyCertificateOutlined />
};

export default function ProformaApprovalsPage() {
  const { me } = useAuth();
  const permissions = me && me.permissions;
  const [tab, setTab] = useState('ACCOUNTS');
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState('');
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  // Decision popup for the selected request. `primed` is the action a
  // quick-action pre-selected (focuses the reason / highlights the button).
  const [modal, setModal] = useState({ open: false, row: null, primed: null });
  const [reason, setReason] = useState('');
  // Stage SALES-RECEIPTS-1 — live customer position + inline receipt state.
  const [position, setPosition] = useState(null);
  const [posBusy, setPosBusy] = useState(false);
  const [ledgers, setLedgers] = useState([]);
  const [rc, setRc] = useState({ amount: null, mode: 'bank', ledgerAccountId: undefined, saving: false });
  const reasonRef = useRef(null);
  const mayRecord = canRecordReceipt(permissions);
  const mayPost = canPostReceipt(permissions);

  const loadCounts = useCallback(async () => {
    try { setCounts(await proformaApprovalApi.inboxCounts() || {}); } catch (_e) { /* non-fatal */ }
  }, []);
  const load = useCallback(async (tabKey) => {
    setLoading(true);
    try {
      const data = await proformaApprovalApi.inbox(inboxFilterForTab(tabKey));
      setRows((data && data.rows) || []);
    } catch (err) {
      message.error(backendError(err, 'Could not load the approval inbox.'));
      setRows([]);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(tab); }, [tab, load]);
  useEffect(() => { loadCounts(); }, [loadCounts]);

  const refresh = useCallback(() => { load(tab); loadCounts(); }, [tab, load, loadCounts]);

  // Load the customer's operational position (for payment-bearing decisions).
  const loadPosition = useCallback(async (row) => {
    if (!row || !row.customerId) { setPosition(null); return; }
    setPosBusy(true);
    try { setPosition(await customerReceiptsApi.position(row.customerId, row.proformaId)); }
    catch (_e) { setPosition(null); }
    finally { setPosBusy(false); }
  }, []);

  // Open the decision popup for a request. `primed` (optional) is the action a
  // row quick-action pre-selected.
  const openRow = useCallback((row, primed) => {
    setReason('');
    setRc({ amount: null, mode: 'bank', ledgerAccountId: undefined, saving: false });
    setPosition(null);
    setModal({ open: true, row, primed: primed || null });
    const showPayment = row && (row.approvalType === 'ACCOUNTS_APPROVAL' || row.approvalType === 'CREDIT_OVERRIDE');
    if (showPayment) {
      loadPosition(row);
      if (!ledgers.length) {
        customerReceiptsApi.cashBankLedgers()
          .then((d) => setLedgers(Array.isArray(d) ? d : ((d && d.rows) || [])))
          .catch(() => setLedgers([]));
      }
    }
  }, [ledgers.length, loadPosition]);
  const closeModal = () => setModal({ open: false, row: null, primed: null });

  // Focus the reason box when a reason-required action was pre-selected.
  useEffect(() => {
    if (modal.open && modal.primed && DECISION[modal.primed] && DECISION[modal.primed].needsReason) {
      const t = setTimeout(() => reasonRef.current && reasonRef.current.focus(), 120);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [modal.open, modal.primed]);

  // Record an advance receipt inline against this proforma, then (if Accounts)
  // post it to the GL so the position refreshes before the decision.
  const recordInlineReceipt = useCallback(async () => {
    const row = modal.row;
    if (!row || !row.customerId) return;
    const amt = Number(rc.amount);
    if (!Number.isFinite(amt) || amt <= 0) { message.warning('Enter a receipt amount.'); return; }
    if (!rc.ledgerAccountId) { message.warning('Pick the deposit (cash/bank) account.'); return; }
    setRc((s) => ({ ...s, saving: true }));
    try {
      const draft = await customerReceiptsApi.create({
        customerId: row.customerId, proformaId: row.proformaId,
        amount: amt, mode: rc.mode, ledgerAccountId: rc.ledgerAccountId,
        receiptDate: new Date().toISOString().slice(0, 10)
      });
      if (mayPost && draft && draft.id) await customerReceiptsApi.post(draft.id);
      message.success(mayPost ? 'Receipt recorded and posted.' : 'Draft receipt recorded (Accounts will post it).');
      setRc({ amount: null, mode: 'bank', ledgerAccountId: undefined, saving: false });
      await loadPosition(row);
    } catch (err) {
      message.error(backendError(err, 'Could not record the receipt.'));
      setRc((s) => ({ ...s, saving: false }));
    }
  }, [modal.row, rc, mayPost, loadPosition]);

  const submit = useCallback(async (kind) => {
    const row = modal.row;
    if (!kind || !row) return;
    const spec = DECISION[kind];
    const r = reason.trim();
    if (spec.needsReason && !r) { message.warning('Add a reason before you continue.'); return; }
    // The payment snapshot stored on the approval request is DERIVED from the
    // live position (no typed figures) — a real record of exposure at decision time.
    const paymentDetails = position ? {
      paymentReceived: position.netMoneyIn > 0,
      advanceReceived: position.thisPiAdvance,
      outstandingAmount: position.outstanding,
      creditLimit: position.creditLimit,
      creditException: position.overLimit
    } : {};
    setBusy(true);
    try {
      const id = row.id;
      if (kind === 'accountsApprove') await proformaApprovalApi.accountsApprove(id, { paymentDetails, remarks: r });
      else if (kind === 'accountsReject') await proformaApprovalApi.accountsReject(id, r);
      else if (kind === 'accountsHold') await proformaApprovalApi.accountsHold(id, r);
      else if (kind === 'accountsEscalate') await proformaApprovalApi.accountsEscalate(id, { reason: r, paymentDetails });
      else if (kind === 'l3Approve') await proformaApprovalApi.l3Approve(id, r);
      else if (kind === 'l3Reject') await proformaApprovalApi.l3Reject(id, r);
      message.success('Decision recorded.');
      closeModal();
      refresh();
    } catch (err) {
      message.error(backendError(err, 'Could not record the decision.'));
    } finally { setBusy(false); }
  }, [modal.row, reason, position, refresh]);

  const columns = useMemo(() => [
    {
      title: 'Proforma', dataIndex: 'proformaNo', key: 'proformaNo', width: 168, ellipsis: true,
      render: (v) => <span className="font-semibold text-slate-700">{v || '—'}</span>
    },
    // Customer has no fixed width — it flexes to fill the row and ellipsizes.
    { title: 'Customer', dataIndex: 'customerName', key: 'customerName', ellipsis: { showTitle: true }, render: (v) => <span className="text-slate-700">{v || '—'}</span> },
    { title: 'Amount', dataIndex: 'grandTotal', key: 'grandTotal', align: 'right', width: 128, render: (v) => <span className="tabular-nums text-slate-700">{money(v)}</span> },
    {
      title: 'Outstanding', dataIndex: 'liveOutstanding', key: 'liveOutstanding', align: 'right', width: 152,
      render: (v, r) => (
        <div className="flex items-center justify-end gap-1.5 tabular-nums whitespace-nowrap">
          {r.liveOverLimit ? <Tag color="volcano" className="!m-0 !px-1 !text-[9px] !leading-4 !font-bold">OVER</Tag> : null}
          <span className={r.liveOverLimit ? 'text-rose-600 font-semibold' : 'text-slate-600'}>{money(v)}</span>
        </div>
      )
    },
    { title: 'Stage', dataIndex: 'approvalLevel', key: 'approvalLevel', width: 104, render: (v) => <Tag color={approvalLevelColor(v)} className="!m-0">{approvalLevelLabel(v)}</Tag> },
    { title: 'Raised', dataIndex: 'requestedAt', key: 'requestedAt', width: 148, ellipsis: true, render: (v) => <span className="text-slate-500 whitespace-nowrap">{dateShort(v)}</span> },
    { title: 'Status', dataIndex: 'status', key: 'status', width: 118, render: (v) => <Tag color={REQ_STATUS_COLOR[v] || 'default'} className="!m-0">{v}</Tag> },
    {
      title: 'Decision', key: 'actions', width: 148, align: 'center',
      render: (_v, row) => {
        const a = approverActionsFor(row, permissions);
        if (!a.approve && !a.reject && !a.hold && !a.escalate) {
          return <span className="text-xs text-slate-300" title="No action for you">—</span>;
        }
        const level = row.approvalLevel;
        const approveKind = level === 'L3' ? 'l3Approve' : 'accountsApprove';
        const rejectKind = level === 'L3' ? 'l3Reject' : 'accountsReject';
        const stop = (e) => e.stopPropagation();
        return (
          <Space size={4} onClick={stop}>
            {a.approve ? <Tooltip title="Approve"><Button size="small" type="primary" icon={<CheckCircleOutlined />} onClick={() => openRow(row, approveKind)} /></Tooltip> : null}
            {a.escalate ? <Tooltip title="Escalate to Management"><Button size="small" icon={<ArrowUpOutlined />} onClick={() => openRow(row, 'accountsEscalate')} /></Tooltip> : null}
            {a.hold ? <Tooltip title="Hold"><Button size="small" icon={<PauseCircleOutlined />} onClick={() => openRow(row, 'accountsHold')} /></Tooltip> : null}
            {a.reject ? <Tooltip title="Reject"><Button size="small" danger icon={<CloseCircleOutlined />} onClick={() => openRow(row, rejectKind)} /></Tooltip> : null}
          </Space>
        );
      }
    }
  ], [permissions, openRow]);

  const tabItems = useMemo(() => APPROVAL_TABS.map((t) => {
    const n = t.countKey ? (counts[t.countKey] || 0) : 0;
    return {
      key: t.key,
      label: t.countKey
        ? <span>{t.label} <Badge count={n} showZero={false} size="small" color={n ? undefined : '#cbd5e1'} style={{ marginInlineStart: 4 }} /></span>
        : t.label
    };
  }), [counts]);

  // Client-side customer / proforma filter over the loaded inbox rows.
  const q = search.trim().toLowerCase();
  const visibleRows = useMemo(() => {
    if (!q) return rows;
    return rows.filter((r) =>
      (r.customerName || '').toLowerCase().includes(q) ||
      (r.proformaNo || '').toLowerCase().includes(q));
  }, [rows, q]);

  const emptyText = q ? `No requests match “${search.trim()}”.`
    : tab === 'ACCOUNTS' ? 'No confirmed proformas waiting on Accounts.'
    : tab === 'L3' ? 'Nothing escalated for a management approval.'
    : 'Nothing here yet.';

  // ── Popup decision context (derived from the selected row) ──────────
  const row = modal.row;
  const a = row ? approverActionsFor(row, permissions) : {};
  const level = row && row.approvalLevel;
  const approveKind = level === 'L3' ? 'l3Approve' : 'accountsApprove';
  const rejectKind = level === 'L3' ? 'l3Reject' : 'accountsReject';
  const spec = DECISION[approveKind];
  const showPayment = row && (row.approvalType === 'ACCOUNTS_APPROVAL' || row.approvalType === 'CREDIT_OVERRIDE');
  const canDecide = !!(a.approve || a.reject || a.hold || a.escalate);
  const terminal = row && !(row.status === 'PENDING' || row.status === 'HELD');

  // One decision-bar button. Kept as a helper so the reason-gating logic
  // (spec.needsReason && !reason.trim()) lives in one place.
  const actionButton = (kind, props = {}) => {
    const spec = DECISION[kind];
    const primed = modal.primed === kind;
    const { className, ...rest } = props;
    return (
      <Button
        {...rest}
        loading={busy}
        disabled={spec.needsReason && !reason.trim()}
        className={[className, primed ? 'ring-2 ring-offset-1 ring-blue-300' : ''].filter(Boolean).join(' ') || undefined}
        onClick={() => submit(kind)}
      >{spec.ok}</Button>
    );
  };

  return (
    <div className="p-4">
      <Card
        title="Proforma Approvals"
        extra={<Button size="small" icon={<ReloadOutlined />} onClick={refresh}>Refresh</Button>}
        styles={{ body: { paddingTop: 12 } }}
      >
        {/* KPI strip — what's waiting, by level. Each card jumps to its tab;
            the active tab's card is accented. */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
          {KPI.map((k) => {
            const accent = KPI_ACCENT[k.level];
            const active = k.tab && tab === k.tab;
            const clickable = !!k.tab;
            return (
              <div key={k.key}
                role={clickable ? 'button' : undefined}
                tabIndex={clickable ? 0 : undefined}
                onClick={clickable ? () => setTab(k.tab) : undefined}
                onKeyDown={clickable ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setTab(k.tab); } } : undefined}
                className={`flex items-center gap-3.5 rounded-xl border bg-white px-4 py-3 transition
                  ${clickable ? 'cursor-pointer hover:border-slate-300 hover:shadow-sm' : ''}`}
                style={{
                  borderColor: active ? accent : '#e2e8f0',
                  boxShadow: active ? `inset 0 0 0 1px ${accent}` : undefined
                }}>
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg text-[19px]"
                  style={{ background: `${accent}14`, color: accent }}>
                  {KPI_ICON[k.key]}
                </span>
                <Statistic
                  className="leading-none"
                  title={<span className="text-[11.5px] font-medium text-slate-500">{k.label}</span>}
                  value={counts[k.key] || 0}
                  valueStyle={{ fontSize: 24, fontWeight: 700, lineHeight: 1.05, color: accent }}
                />
              </div>
            );
          })}
        </div>

        <Tabs
          activeKey={tab}
          onChange={setTab}
          items={tabItems}
          tabBarExtraContent={
            <Input
              allowClear
              size="small"
              prefix={<SearchOutlined className="text-slate-400" />}
              placeholder="Search customer / proforma…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ width: 244 }}
            />
          }
        />
        <Table
          className="approval-grid"
          size="small"
          rowKey="id"
          loading={loading}
          columns={columns}
          dataSource={visibleRows}
          scroll={{ x: 1160 }}
          rowClassName={() => 'cursor-pointer'}
          onRow={(record) => ({ onClick: () => openRow(record) })}
          pagination={{ pageSize: 20, showSizeChanger: true, size: 'small', showTotal: (t) => `${t} request${t === 1 ? '' : 's'}` }}
          locale={{ emptyText: <Empty description={emptyText} /> }}
        />
      </Card>

      <Modal
        open={modal.open}
        onCancel={closeModal}
        width={700}
        centered
        destroyOnClose
        title={row ? (
          <span className="text-[15px] font-semibold text-slate-800">
            Proforma Approval <span className="text-slate-400 font-normal">· {row.proformaNo}</span>
          </span>
        ) : 'Decision'}
        footer={canDecide ? (
          <div className="flex justify-end gap-2">
            {a.reject ? actionButton(rejectKind, { danger: true, icon: <CloseCircleOutlined /> }) : null}
            {a.hold ? actionButton('accountsHold', { icon: <PauseCircleOutlined /> }) : null}
            {a.escalate ? actionButton('accountsEscalate', { icon: <ArrowUpOutlined /> }) : null}
            {a.approve ? actionButton(approveKind, { type: 'primary', icon: <CheckCircleOutlined /> }) : null}
          </div>
        ) : null}
        styles={{ body: { paddingTop: 10 } }}
      >
        {row ? (
          <>
            {/* Header row — customer + amount + status strip. */}
            <div className="flex items-baseline justify-between gap-3">
              <span className="text-[13.5px] font-medium text-slate-700 truncate">{row.customerName || '—'}</span>
              <span className="text-[14px] font-semibold text-slate-800 tabular-nums whitespace-nowrap">{money(row.grandTotal)}</span>
            </div>
            <div className="flex items-center gap-1.5 flex-wrap mt-1.5 mb-3 pb-3 border-b border-slate-100">
              <Tag color={approvalLevelColor(level)} className="!m-0">{approvalLevelLabel(level)}</Tag>
              <Tag color={REQ_STATUS_COLOR[row.status] || 'default'} className="!m-0">{row.status}</Tag>
              {row.liveOverLimit ? <Tag color="volcano" className="!m-0">Over limit</Tag> : null}
              <span className="text-[11px] text-slate-400 whitespace-nowrap ml-auto">Requested: {requestedActionLabel(row.requestedAction)} · {dateTime(row.requestedAt)}</span>
            </div>

            {/* Two-column body — live position (left) + request detail (right). */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-5 gap-y-3">
              {/* Live customer position — real exposure, not typed figures. Shown
                  for payment-bearing reviews (spec && spec.payment) / Accounts /
                  credit-override reviews. */}
              {showPayment || (spec && spec.payment) ? (
                <div>
                  <div className={SEC + ' mb-1.5'}>Customer position{posBusy ? <span className="normal-case font-medium tracking-normal"> · loading…</span> : null}</div>
                  <div className="rounded-lg border border-slate-200 divide-y divide-slate-100">
                    <PosRow label="Outstanding" value={money(position && position.outstanding)}
                      tone={position && position.overLimit ? 'danger' : 'strong'} />
                    <PosRow label="Credit limit" value={position && position.creditLimitSet ? money(position.creditLimit) : 'Not set'} />
                    <PosRow label="Confirmed dues" value={money(position && position.confirmedDues)} />
                    <PosRow label="Received (net)" value={money(position && position.netMoneyIn)} />
                    <PosRow label="Advance (this PI)" value={money(position && position.thisPiAdvance)} />
                    <PosRow label="On-account advance" value={money(position && position.onAccountAdvance)} />
                  </div>
                  {overLimitMessage(position) ? (
                    <Alert type="warning" showIcon className="mt-2" message={<span className="text-[12px]">{overLimitMessage(position)}</span>} />
                  ) : null}
                </div>
              ) : null}

              {/* Request facts. */}
              <div>
                <div className={SEC + ' mb-1.5'}>Approval detail</div>
                <RequestDetail row={row} />
              </div>
            </div>

            {/* Inline receipt against this proforma (PI advance). */}
            {showPayment && mayRecord && position && !terminal ? (
              <div className="mt-3 rounded-lg border border-slate-200 px-2.5 py-2">
                <div className={SEC + ' mb-1.5'}>Record advance receipt</div>
                <div className="flex flex-wrap items-center gap-2">
                  <InputNumber size="small" min={0} controls={false} prefix="₹" placeholder="Amount" style={{ width: 122 }}
                    value={rc.amount} onChange={(v) => setRc((s) => ({ ...s, amount: v }))} />
                  <Select size="small" style={{ width: 104 }} value={rc.mode}
                    onChange={(v) => setRc((s) => ({ ...s, mode: v }))}
                    options={RECEIPT_MODES.map((m) => ({ value: m, label: modeLabel(m) }))} />
                  <Select size="small" style={{ minWidth: 180, flex: 1 }} placeholder="Deposit account" showSearch optionFilterProp="label"
                    value={rc.ledgerAccountId} onChange={(v) => setRc((s) => ({ ...s, ledgerAccountId: v }))}
                    options={ledgers.map((l) => ({ value: l.id, label: l.accountName || l.account_name || l.id }))} />
                  <Button size="small" type="primary" ghost icon={<PlusOutlined />} loading={rc.saving} onClick={recordInlineReceipt}>
                    {mayPost ? 'Record & post' : 'Save draft'}
                  </Button>
                </div>
              </div>
            ) : null}

            {/* Decision reason / remarks. */}
            {canDecide ? (
              <div className="mt-3">
                <div className="text-[12px] font-medium text-slate-500 mb-1">
                  {a.approve && !a.reject && !a.hold ? 'Remarks (optional)' : 'Reason (required for Hold / Reject / Escalate)'}
                </div>
                <Input.TextArea
                  ref={reasonRef} rows={2} maxLength={500} showCount
                  placeholder="Type a short reason…"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                />
              </div>
            ) : null}
          </>
        ) : null}
      </Modal>
    </div>
  );
}

// Request facts the approver needs before deciding (shown in the popup). Compact
// bordered list; empty payment-snapshot rows are hidden until a decision is made.
function RequestDetail({ row }) {
  const money2 = (v) => (v == null ? '—' : money(v));
  const showPayment = row.approvalType === 'ACCOUNTS_APPROVAL' || row.approvalType === 'CREDIT_OVERRIDE';
  const terminal = !(row.status === 'PENDING' || row.status === 'HELD');
  return (
    <div className="rounded-lg border border-slate-200 divide-y divide-slate-100">
      {/* "Status change" is only meaningful for a real transition — a review
          request (L2/L3) carries old===requested (both CONFIRMED), so hide it.
          "Requested" is already shown in the popup header. */}
      {row.oldStatus && row.oldStatus !== row.requestedStatus
        ? <Field label="Status change">{row.oldStatus} → {row.requestedStatus}</Field> : null}
      <Field label="Reason">{row.reason || '—'}</Field>
      {showPayment && row.creditException ? <Field label="Credit exception">Yes</Field> : null}
      {row.accountantRemarks ? <Field label="Accountant remarks">{row.accountantRemarks}</Field> : null}

      {showPayment && terminal ? (
        <>
          <Field label="Payment received">{row.paymentReceived == null ? '—' : (row.paymentReceived ? 'Yes' : 'No')}</Field>
          <Field label="Advance received">{money2(row.advanceReceived)}</Field>
          <Field label="Outstanding (at decision)">{money2(row.outstandingAmount)}</Field>
        </>
      ) : null}

      {row.status === 'REJECTED' && row.rejectionReason ? <Field label="Rejection reason">{row.rejectionReason}</Field> : null}
      {row.superAdminReason ? <Field label="Management reason">{row.superAdminReason}</Field> : null}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="flex items-start justify-between gap-3 px-2.5 py-1.5">
      <span className="text-[12px] text-slate-500 whitespace-nowrap">{label}</span>
      <span className="text-[13px] text-slate-700 text-right break-words">{children}</span>
    </div>
  );
}

// One line of the popup's live customer-position panel. `tone`: danger (over
// limit) / strong (the headline Outstanding).
function PosRow({ label, value, tone }) {
  const cls = tone === 'danger' ? 'text-rose-600 font-semibold'
    : tone === 'strong' ? 'text-slate-900 font-semibold' : 'text-slate-700';
  return (
    <div className="flex items-center justify-between px-2.5 py-1.5">
      <span className="text-[12px] text-slate-500">{label}</span>
      <span className={`text-[13px] tabular-nums ${cls}`}>{value}</span>
    </div>
  );
}
